import React, { useEffect, useRef, useState } from "react";

import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { ItemTypes as IItemTypes } from "../../../../../../../models/item.types";
import { Row } from "../../../../../../../models/shipeezi.table";
import { handleGetConvertValue } from "../../../../../../../util/converts";
import { Status } from "../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../util/getMeasurement";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import ItemTypeModal from "./ItemTypeModal";
import useStyles, { OrderOption } from "./styles";
import useActions from "./useActions";
import { useAppState } from "Context/StateContext";
import { isShowAction } from "Util/permissions";
import { Branch } from "Models/branch";
import {
  MoreOptions,
  MoreOptionsComponentHandles,
} from "Components/MoreOptionsComponent/MoreOptions";

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
  };
};

const ItemTypes = ({ permission }: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const {
    itemTypeList,
    hasErrors,
    page,
    pageSize,
    total,
    isLoading,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createItemType,
    updateItemType,
  } = useActions();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedItemType, setSelectedItemType] = useState<IItemTypes>();

  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);

  const branchesMenuRef = useRef<MoreOptionsComponentHandles>(null);

  const badgeStatus = (status: string) => (
    <Badge
      className={`badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
        `}
    >
      {status}
    </Badge>
  );

  const editItemType = (index: number) => {
    const itemTypeToUpdate = itemTypeList.find((itemType) => Number(itemType.id) === Number(index));

    if (itemTypeToUpdate) {
      if (hasErrors !== undefined) setHasErrors(undefined);
      setSelectedItemType(itemTypeToUpdate);
      setOpenModal(true);
    }
  };

  const changeStatusItemType = (index: number) => {
    const typeToUpdate = itemTypeList.find((itemType) => Number(itemType.id) === Number(index));

    if (typeToUpdate) {
      updateItemType({
        id: Number(index),
        itemType: {
          ...typeToUpdate,
          status: typeToUpdate.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          customColor={Color.INFO}
          className={classes.changeStatusButton}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveItemTypeSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, itemTypeList);

    setRows([...rowsSelected]);
  };

  const getItemTypeBranchesReferences = (branches?: Branch[]) => {
    if (!branches || branches.length === 0) {
      return [];
    }

    const branchesSorted = [...branches]
      .filter((b) => !!b?.name?.trim())
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "pt-BR"));

    return (
      <MoreOptions
        ref={branchesMenuRef}
        title={translate("moreBranches")}
        manyOptionsToShow={4}
        singleOptionPerLine={true}
        data={branchesSorted}
        returnLabel={(branch: Branch) => branch?.name ?? ""}
        asComponent={(label: string) => (
          <OrderOption onClick={() => branchesMenuRef.current?.setAnchor(null)}>
            {label}
          </OrderOption>
        )}
      />
    );
  };

  const columns: Column[] = [
    { name: "code", title: translate("code") },
    { name: "name", title: translate("name") },
    {
      name: "branches",
      title: translate("branch"),
      hideSearch: true,
      formatComponent: (branches?: Branch[]) => getItemTypeBranchesReferences(branches),
    },
    {
      name: "volume",
      title: translate("volume"),
      formatText: (volume: number) =>
        `${handleGetConvertValue(
          volume,
          getMeasurement()?.code,
          getMeasurement()?.decimalPlaces
        )} ${getMeasurement()?.code}`,
    },
    {
      name: "isBox",
      hideSearch: true,
      title: translate("isBox"),
      formatText: (isBox: boolean) => (isBox ? translate("yes") : translate("no")),
    },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord:
      permission && isShowAction(currentUser, permission.create)
        ? {
            text: translate("create"),
            action: () => {
              if (hasErrors !== undefined) setHasErrors(undefined);
              setSelectedItemType(undefined);
              setOpenModal(true);
            },
            tooltip: translate("create"),
          }
        : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("isBox"),
          column: "isBox",
          options: [
            { label: "Yes", value: "1" },
            { label: "No", value: "0" },
          ],
        },
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        { name: translate("name"), column: "name" },
        { name: translate("code"), column: "code" },
        { name: translate("volume"), column: "volume" },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: renderButtonToChangeStatus(),
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "react.query.item.success",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="itemTypeListTable">
      <ShipeeziNewGrid
        data={itemTypeList}
        columns={columns}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit:
            permission && isShowAction(currentUser, permission.edit)
              ? (itemTypeId) => editItemType(Number(itemTypeId))
              : undefined,
          handleChange: (itemTypeId) => changeStatusItemType(Number(itemTypeId)),
        }}
        clearSelection={clearSelection}
        handleSelect={(rowsArr: number[]) => handleSaveItemTypeSelected(rowsArr)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      {openModal && (
        <ItemTypeModal
          open={openModal}
          hasError={hasErrors}
          itemTypeToUpdate={selectedItemType}
          isLoading={isLoading}
          closeModal={() => setOpenModal(false)}
          updateItemType={(itemType: IItemTypes) =>
            itemType.id && updateItemType({ id: itemType.id, itemType })
          }
          createItemType={(itemType: IItemTypes) => createItemType(itemType)}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId && status) {
              updateItemType({ id: rowId, itemType: { status } as IItemTypes });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default ItemTypes;
